import React, { useEffect, useMemo, useState } from "react";
import { api, inr, formatApiError } from "@/lib/api";
import { useProjects } from "@/contexts/ProjectContext";
import { useAuth } from "@/contexts/AuthContext";
import { INVENTORY } from "@/constants/testIds";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Download, UploadCloud, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  available: "bg-[#EEF1EE] text-[#2F4A3D] border-[#C9D3CC]",
  held: "bg-[#F8EADB] text-[#9A5B26] border-[#EBCBA8]",
  booked: "bg-[#F6E1DA] text-[#B4472A] border-[#E9B7A7]",
  sold: "bg-[#DDE3E0] text-forest border-forest/40",
};

const floorLabel = (f) => {
  if (f === 0) return "Ground floor";
  const s = ["th", "st", "nd", "rd"], v = f % 100;
  return `${f}${s[(v - 20) % 10] || s[v] || s[0]} floor`;
};

const STATUS_ORDER = ["available", "held", "booked", "sold"];

const HEADER_ALIASES = {
  tower: ["tower", "tower_name", "block", "wing", "building"],
  floor: ["floor", "floor_no", "floor_number", "level"],
  unit_no: ["unit_no", "unit", "unit_number", "unitno", "flat_no", "flat", "flat_number", "unit_id"],
  config: ["config", "configuration", "configurat", "bhk", "type", "unit_type", "flat_type"],
  carpet_area: ["carpet_area", "carpet", "carpet_are", "carpet_area_sqft", "carpet_area_sq_ft", "rera_carpet_area"],
  super_built_up_area: ["super_built_up_area", "super_builtup_area", "super_built_up", "super_built", "sba", "super_area", "super_built_up_area_sqft", "super_built_up_area_sq_ft", "saleable_area"],
  price: ["price", "total_price", "amount", "cost", "agreement_value"],
  facing: ["facing", "direction"],
  status: ["status", "availability", "unit_status"],
};
const normKey = (h) => h.replace(/^\uFEFF/, "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
const PREFIX = [["super", "super_built_up_area"], ["sba", "super_built_up_area"], ["carpet", "carpet_area"], ["config", "config"], ["bhk", "config"], ["unit_n", "unit_no"], ["flat_n", "unit_no"], ["tower", "tower"], ["floor", "floor"], ["price", "price"], ["facing", "facing"], ["status", "status"]];
const fieldOf = (h) => { const k = normKey(h); return Object.keys(HEADER_ALIASES).find((f) => HEADER_ALIASES[f].includes(k)) || PREFIX.find(([p]) => k.startsWith(p))?.[1]; };

function splitCsvLine(line) {
  const out = []; let cur = ""; let q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
    else if (c === "," && !q) { out.push(cur); cur = ""; }
    else cur += c;
  }
  out.push(cur);
  return out.map((x) => x.trim());
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.replace(/,/g, "").trim());
  if (lines.length === 0) return [];
  const fields = splitCsvLine(lines[0]).map(fieldOf);
  return lines.slice(1).map((line) => {
    const cells = splitCsvLine(line);
    const row = {};
    fields.forEach((f, i) => { if (f && !row[f]) row[f] = cells[i] || ""; });
    return row;
  });
}

const num = (v) => { const n = parseFloat(String(v || "").replace(/[^\d.]/g, "")); return Number.isFinite(n) ? n : null; };
const statusOf = (v) => {
  const s = String(v || "").trim().toLowerCase();
  if (!s) return null;
  if (/unsold|not\s*sold/.test(s)) return "available";
  if (/sold|registered|handed|possession/.test(s)) return "sold";
  if (/book|token|allot|agreement|confirm|reg/.test(s)) return "booked";
  if (/hold|held|block|reserv|not\s*avail|unavail|n\/a|mortgage|landowner|owner|freez/.test(s)) return "held";
  if (/avail|open|vacant|free|unbooked|ready/.test(s)) return "available";
  return null;
};
const ORDINALS = ["ground", "first", "second", "third", "fourth", "fifth", "sixth", "seventh", "eighth", "ninth", "tenth"];
const floorOf = (v, unitNo) => {
  const s = String(v || "").trim().toLowerCase();
  if (/^(g|gf|ug|ground|stilt|lobby)/.test(s)) return 0;
  const w = ORDINALS.findIndex((o) => s.startsWith(o));
  if (w >= 0) return w;
  const n = num(s);
  if (n !== null) return Math.trunc(n);
  const d = String(unitNo || "").match(/(\d+)(?!.*\d)/)?.[1];
  return d && d.length >= 3 ? Number(d.slice(0, -2)) : 0;
};

const toUnitRow = (r) => ({
  tower: String(r.tower || "").trim() || "Main", floor: floorOf(r.floor, r.unit_no), unit_no: String(r.unit_no || "").trim(),
  config: String(r.config || "").trim(), carpet_area: num(r.carpet_area), super_built_up_area: num(r.super_built_up_area),
  price: num(r.price), facing: r.facing || null, status: statusOf(r.status),
});

function UnitEditor({ unit, onSaved, canEdit }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  useEffect(() => {
    if (open) setForm({
      tower: unit.tower || "", floor: unit.floor ?? 1, unit_no: unit.unit_no || "",
      config: unit.config || "", carpet_area: unit.carpet_area ?? "", super_built_up_area: unit.super_built_up_area ?? "",
      price: unit.price ?? "", facing: unit.facing || "", status: unit.status || "available",
    });
  }, [open, unit]);
  const submit = async () => {
    try {
      const body = { ...form };
      body.floor = Number(body.floor);
      body.carpet_area = body.carpet_area === "" ? null : Number(body.carpet_area);
      body.super_built_up_area = body.super_built_up_area === "" ? null : Number(body.super_built_up_area);
      body.price = body.price === "" ? null : Number(body.price);
      Object.keys(body).forEach((k) => { if (body[k] === null || body[k] === "") delete body[k]; });
      await api.patch(`/units/${unit.id}`, body);
      toast.success(`${form.unit_no} saved`);
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => canEdit && setOpen(v)}>
      <DialogTrigger asChild>
        <button
          data-testid={INVENTORY.unitCell(unit.id)}
          className={`w-full min-w-0 h-full border rounded-sm px-3 sm:px-4 py-3 text-left transition-[border-color,box-shadow] duration-150 hover:border-forest hover:shadow-sm ${STATUS_COLORS[unit.status] || ""}`}
          title={`${unit.unit_no} · ${unit.status} · ${inr(unit.price)} · click to edit`}
        >
          <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1 min-w-0">
            <div className="text-[13px] uppercase tracking-[0.15em] font-bold leading-none break-all">{unit.unit_no}</div>
            {unit.price ? <div data-testid={`inv-unit-price-${unit.id}`} className="text-[13px] font-bold leading-none tabular-nums">{inr(unit.price)}</div> : null}
          </div>
          <div className="text-xs mt-1.5 opacity-80 break-words">{unit.config}</div>
          <div data-testid={`inv-unit-area-${unit.id}`} className="text-[11px] mt-1.5 opacity-75 leading-snug flex flex-wrap gap-x-1">
            <span className="whitespace-nowrap">Carpet {unit.carpet_area ?? "—"}</span>
            <span aria-hidden>·</span>
            <span className="whitespace-nowrap">Built-up {unit.super_built_up_area ?? "—"} sqft</span>
          </div>
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">Edit unit {unit.unit_no}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <F label="Tower" value={form.tower} onChange={(v) => setForm({ ...form, tower: v })} />
          <F label="Floor" type="number" value={form.floor} onChange={(v) => setForm({ ...form, floor: v })} />
          <F label="Unit no" value={form.unit_no} onChange={(v) => setForm({ ...form, unit_no: v })} />
          <F label="Config" value={form.config} onChange={(v) => setForm({ ...form, config: v })} />
          <F label="Carpet area (sqft)" type="number" value={form.carpet_area} onChange={(v) => setForm({ ...form, carpet_area: v })} />
          <F label="Super built-up area (sq.ft)" type="number" value={form.super_built_up_area} onChange={(v) => setForm({ ...form, super_built_up_area: v })} />
          <F label="Price (₹)" type="number" value={form.price} onChange={(v) => setForm({ ...form, price: v })} />
          <F label="Facing" value={form.facing} onChange={(v) => setForm({ ...form, facing: v })} />
          <div>
            <div className="label-caps mb-1.5">Status</div>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="h-10 rounded-sm border-[#E6E4DD]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <button data-testid="unit-save-btn" onClick={submit} className="h-9 px-4 rounded-sm bg-forest text-white text-sm font-medium hover:bg-forest-soft transition-colors duration-150">Save unit</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <div className="label-caps mb-1.5">{label}</div>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full h-10 border border-[#E6E4DD] rounded-sm px-3 text-sm focus:outline-none focus:border-forest" />
    </div>
  );
}

function StatusMapper({ values, map, setMap }) {
  if (!values.length) return <div data-testid="inv-import-no-status" className="text-xs text-[#9A5B26] bg-[#F8EADB] border border-[#EBCBA8] rounded-sm p-2.5">No Status column found in this file. New units will be marked available; existing units keep their current status.</div>;
  return (
    <div className="space-y-1.5" data-testid="inv-import-status-map">
      <div className="label-caps">Status in file → status in Propzel</div>
      <div className="max-h-44 overflow-y-auto space-y-1.5 pr-1">
        {values.map(([raw, count]) => (
          <div key={raw} className="flex items-center gap-2">
            <div className="flex-1 text-sm text-forest truncate">"{raw}" <span className="text-forest/50 text-xs">· {count}</span></div>
            <Select value={map[raw]} onValueChange={(v) => setMap({ ...map, [raw]: v })}>
              <SelectTrigger data-testid={`inv-status-map-${raw}`} className="w-[130px] h-8 rounded-sm border-[#E6E4DD] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
      </div>
    </div>
  );
}

function ImportDialog({ projectId, onDone }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const [statusMap, setStatusMap] = useState({});
  const statusValues = useMemo(() => {
    const c = {};
    rows.forEach((r) => { const v = String(r.status || "").trim(); if (v) c[v] = (c[v] || 0) + 1; });
    return Object.entries(c).sort((a, b) => b[1] - a[1]);
  }, [rows]);
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result));
      const m = {};
      parsed.forEach((r) => { const v = String(r.status || "").trim(); if (v && !m[v]) m[v] = statusOf(v) || "available"; });
      setStatusMap(m);
      setRows(parsed);
    };
    reader.readAsText(f);
  };
  const submit = async () => {
    if (!projectId) { toast.error("Pick a project first"); return; }
    setBusy(true);
    try {
      const body = {
        project_id: projectId,
        replace_existing: replace,
        rows: rows.map((r) => ({ ...toUnitRow(r), status: statusMap[String(r.status || "").trim()] || null })).filter((r) => r.unit_no),
      };
      const invalid = rows.length - body.rows.length;
      if (!body.rows.length) { toast.error("No rows with a unit number found — check the column headers"); return; }
      const { data } = await api.post("/units/import", body);
      const extra = [data.updated && `${data.updated} existing updated`, (data.failed + invalid) && `${data.failed + invalid} invalid`].filter(Boolean).join(", ");
      toast.success(`Imported ${data.created}${replace ? " (replaced existing)" : ""}${extra ? ` · ${extra}` : ""}`);
      setOpen(false); setRows([]); setFileName("");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="inv-import-btn" className="h-9 rounded-sm border border-[#E6E4DD] text-sm text-forest hover:border-forest transition-colors duration-150 inline-flex items-center gap-2 px-3">
          <UploadCloud className="h-4 w-4" /> Import CSV
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">Import units</DialogTitle></DialogHeader>
        <div className="text-xs text-forest/60">Required: <code>unit_no</code>. Also read: <code>tower, floor, configuration, carpet area, super built up area, price, facing, status</code> — header spelling and case don't matter. Units already in the project are updated from the file.</div>
        <label className="block border-2 border-dashed border-[#E6E4DD] rounded-sm p-6 text-center hover:border-forest transition-colors duration-150 cursor-pointer">
          <input type="file" accept=".csv" onChange={onFile} className="hidden" data-testid="inv-import-file" />
          <UploadCloud className="h-6 w-6 mx-auto text-forest/60 mb-2" />
          <div className="text-sm text-forest">{fileName || "Drop CSV or click to browse"}</div>
          {rows.length > 0 && <div className="text-xs text-forest/50 mt-1">{rows.length} rows detected</div>}
        </label>
        {rows.length > 0 && <StatusMapper values={statusValues} map={statusMap} setMap={setStatusMap} />}
        <div className="flex items-center gap-3">
          <Switch checked={replace} onCheckedChange={setReplace} />
          <div className="text-sm text-forest">Replace all existing units in this project</div>
        </div>
        <DialogFooter>
          <button data-testid="inv-import-submit" disabled={busy || rows.length === 0} onClick={submit} className="h-9 px-4 rounded-sm bg-forest text-white text-sm font-medium hover:bg-forest-soft transition-colors duration-150 disabled:opacity-50">
            {busy ? "Importing…" : `Import ${rows.length} units`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Inventory() {
  const { user } = useAuth();
  const { projects, activeId } = useProjects();
  const [units, setUnits] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState(activeId && !["__all__", "__none__"].includes(activeId) ? activeId : (projects[0]?.id || ""));
  const canEdit = user?.role === "admin" || user?.role === "manager";

  useEffect(() => {
    if (!projectFilter && projects.length > 0) setProjectFilter(projects[0].id);
  }, [projects, projectFilter]);

  const load = async () => {
    if (!projectFilter) { setUnits([]); return; }
    const { data } = await api.get("/units", { params: { project_id: projectFilter } });
    setUnits(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectFilter]);

  const filtered = useMemo(() => units.filter((u) => !statusFilter || u.status === statusFilter), [units, statusFilter]);

  const byTower = useMemo(() => {
    const g = {};
    filtered.forEach((u) => {
      const t = u.tower || "A";
      g[t] = g[t] || {};
      g[t][u.floor] = g[t][u.floor] || [];
      g[t][u.floor].push(u);
    });
    Object.keys(g).forEach((t) => {
      Object.keys(g[t]).forEach((f) => g[t][f].sort((a, b) => (a.unit_no || "").localeCompare(b.unit_no || "")));
    });
    return g;
  }, [filtered]);

  const counts = useMemo(() => {
    const c = { available: 0, held: 0, booked: 0, sold: 0 };
    units.forEach((u) => { if (c[u.status] != null) c[u.status]++; });
    return c;
  }, [units]);

  const exportCsv = () => {
    if (!projectFilter) { toast.error("Pick a project first"); return; }
    window.location.href = `${BACKEND}/api/units/export?project_id=${projectFilter}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-caps">Inventory</div>
          <h2 className="font-display font-black text-3xl text-forest tracking-tight mt-1">Unit matrix</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-full sm:w-[220px] h-9 rounded-sm border-[#E6E4DD]"><SelectValue placeholder="Pick project" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter || "__all__"} onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid={INVENTORY.statusFilter} className="w-[140px] h-9 rounded-sm border-[#E6E4DD]"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <button data-testid="inv-export-btn" onClick={exportCsv} className="h-9 rounded-sm border border-[#E6E4DD] text-sm text-forest hover:border-forest transition-colors duration-150 inline-flex items-center gap-2 px-3">
            <Download className="h-4 w-4" /> Export CSV
          </button>
          {canEdit && <ImportDialog projectId={projectFilter} onDone={load} />}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        {STATUS_ORDER.map((s) => (
          <div key={s} className={`inline-flex items-center gap-2 border rounded-sm px-3 py-1.5 ${STATUS_COLORS[s]}`}>
            <div className="h-2 w-2 rounded-full bg-current opacity-70" />
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold">{s}</span>
            <span className="text-sm font-display font-bold tabular-nums">{counts[s]}</span>
          </div>
        ))}
      </div>

      <div className="space-y-8">
        {Object.entries(byTower).map(([tower, floors]) => {
          const floorNums = Object.keys(floors).map(Number).sort((a, b) => a - b);
          return (
            <div key={tower} className="border border-[#E6E4DD] bg-white rounded-sm p-3 sm:p-5">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                <h3 className="font-display font-bold text-xl text-forest tracking-tight">Tower {tower}</h3>
                <div className="text-xs text-forest/50">{Object.values(floors).flat().length} units · click a cell to edit</div>
              </div>
              <div className="space-y-3">
                {floorNums.map((f) => (
                  <div key={f} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-6">
                    <div className="sm:w-24 shrink-0 sm:pt-4" data-testid={`inv-floor-label-${tower}-${f}`}>
                      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-forest/70">{floorLabel(f)}</div>
                      <div className="text-[10px] text-forest/45 mt-1">{floors[f].length} units · {floors[f].filter((u) => u.status === "available").length} avail.</div>
                    </div>
                    <div className="flex-1 min-w-0 grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] xl:grid-cols-6 gap-2.5">
                      {floors[f].map((u) => (
                        <UnitEditor key={u.id} unit={u} onSaved={load} canEdit={canEdit} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {Object.keys(byTower).length === 0 && (
          <div className="border border-dashed border-[#E6E4DD] rounded-sm p-12 text-center text-forest/50 text-sm">
            No units for this project yet. {canEdit && "Use Import CSV to bulk-load."}
          </div>
        )}
      </div>
    </div>
  );
}
